/**
 * Divorce Certificate Printing Page
 * View and manage approved divorce certificate applications ready for printing
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../contexts/PageTitleContext";
import { certificateService } from "../services/api/certificate.service";
import { UserStatusId } from "../types/user.types";
import { ApplicationCategory } from "../types/certificate.types";
import BusyOverlay from "../components/common/BusyOverlay";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import "../styles/pages/Users.css";

interface DivorcePrintRow {
  certificateApplicationId: number;
  formNo: string;
  category: string;
  countyName: string;
  email: string;
  placeOfWedding: string;
  dateOfWedding: string;
  statusId?: number;
  statusName: string;
  plaintiffName?: string;
  defendantName?: string;
}

export const DivorcePrint: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [applications, setApplications] = useState<DivorcePrintRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [countyFilter, setCountyFilter] = useState<string>("all");

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle(
      "Divorce Certificate Printing",
      "Print approved divorce certificates",
    );
    fetchData();
  }, [setPageTitle]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");

      const data =
        await certificateService.getCertificateApplicationsByCategory(
          ApplicationCategory.Divorce,
        );
      const divorceApplications = Array.isArray(data) ? data : [];

      const rows: DivorcePrintRow[] = divorceApplications.map((app) => ({
        certificateApplicationId: app.id || 0,
        formNo: app.formNo || (app.id ? `DIV-${app.id}` : "DIV-"),
        category: app.category || "Divorce",
        countyName: app.countyName || "-",
        email: app.email || "-",
        placeOfWedding: app.placeOfWedding || "-",
        dateOfWedding: app.dateOfWedding || "",
        statusId: app.statusId,
        statusName: app.statusName || app.status?.name || "",
        plaintiffName: app.plaintiffName,
        defendantName: app.defendantName,
      }));

      setApplications(rows);
    } catch (err: any) {
      console.error("Error fetching applications:", err);
      setError(err?.message || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const availableCounties = Array.from(
    new Set(
      applications
        .map((app) => app.countyName)
        .filter((county) => county && county !== "-") as string[],
    ),
  );

  const filteredApplications = applications.filter((app) => {
    const term = searchTerm.trim().toLowerCase();
    const divorceDateText = app.dateOfWedding
      ? new Date(app.dateOfWedding).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        })
      : "";

    const searchableValues = [
      app.formNo,
      app.plaintiffName,
      app.defendantName,
      app.countyName,
      app.email,
      app.placeOfWedding,
      divorceDateText,
      app.statusName,
    ];

    const matchesSearch =
      term.length === 0 ||
      searchableValues.some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(term),
      );

    const matchesCounty =
      countyFilter === "all" || app.countyName === countyFilter;

    const matchesStatus =
      app.statusId === UserStatusId.Approved ||
      app.statusId === UserStatusId.Completed;

    return matchesSearch && matchesCounty && matchesStatus;
  });

  if (!HasPermission(ModuleId.Divorces, ApplicationPermissionId.Print)) {
    return (
      <div
        className={`dashboard-body ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <Topbar
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>
              You do not have permission to print divorce certificates
            </span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="users-page">
          <BusyOverlay isVisible={loading} message="Loading applications..." />

          <div className="users-content">
            <div className="users-header">
              <input
                type="text"
                className="search-input"
                placeholder="Search by Form No, Plaintiff, Defendant, County, Email, Place of Divorce, Date of Divorce, or Status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select
                className="filter-select"
                value={countyFilter}
                onChange={(e) => setCountyFilter(e.target.value)}
                style={{
                  padding: "0.5rem 1rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                  minWidth: "150px",
                }}
              >
                <option value="all">All Counties</option>
                {availableCounties.map((county) => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
                <button onClick={() => setError("")} className="alert-close">
                  ✕
                </button>
              </div>
            )}

            <div className="users-table-container">
              {filteredApplications.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state__icon">🖨️</div>
                  <p className="empty-state__text">
                    {searchTerm || countyFilter !== "all"
                      ? "No applications found matching your criteria"
                      : "No approved applications ready for printing"}
                  </p>
                </div>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Form No</th>
                      <th>Plaintiff</th>
                      <th>Defendant</th>
                      <th>County</th>
                      <th>Email</th>
                      <th>Place of Divorce</th>
                      <th>Date of Divorce</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((app) => (
                      <tr key={app.certificateApplicationId}>
                        <td>{app.formNo}</td>
                        <td>{app.plaintiffName || "-"}</td>
                        <td>{app.defendantName || "-"}</td>
                        <td>{app.countyName || "-"}</td>
                        <td>{app.email || "-"}</td>
                        <td>{app.placeOfWedding || "-"}</td>
                        <td>
                          {app.dateOfWedding
                            ? new Date(app.dateOfWedding).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "2-digit",
                                },
                              )
                            : "-"}
                        </td>
                        <td>
                          <span className="status-badge status-badge--success">
                            {app.statusName || "Approved"}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              className="btn-icon"
                              onClick={() =>
                                navigate(
                                  `/divorce/print/${app.certificateApplicationId}`,
                                )
                              }
                              title="View Detail"
                            >
                              👁️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DivorcePrint;
